'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { type VariantProps, cva } from 'class-variance-authority'
import { Separator as AriaSeparator } from 'react-aria-components'

const dividerCva = cva('border-brand-2-200', {
  variants: {
    direction: {
      horizontal: 'w-full border-t-px hd:border-t-1.5',
      vertical: 'h-full border-l-px hd:border-l-1.5',
    },
    variant: {
      line: 'border-solid',
      dot: 'border-dashed',
    },
  },
  defaultVariants: {
    direction: 'horizontal',
    variant: 'line',
  },
})

export type DividerVariantProps = RequiredProps<
  VariantProps<typeof dividerCva>,
  'variant' | 'direction'
>

export type DividerProps = WithTestId<
  DividerVariantProps & {
    /** Optional id applied to the root element */
    id?: string
    /** Additional classes to add to the component */
    className?: string
    /** Accessibility */
    'aria-label'?: React.AriaAttributes['aria-label']
    'aria-hidden'?: React.AriaAttributes['aria-hidden']
  }
>

export function Divider({
  direction,
  variant,
  className,
  id,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
}: DividerProps): React.ReactElement {
  return (
    <AriaSeparator
      id={id}
      orientation={direction ?? 'horizontal'}
      aria-label={ariaLabel}
      className={twMerge(dividerCva({ direction, variant }), className)}
      data-testid={dataTestId ?? 'divider'}
    />
  )
}
